import { useEffect, useRef, useState } from "react";
import {
  LuClipboardList,
  LuMonitorSmartphone,
  LuRotateCw,
} from "react-icons/lu";
import {
  DashboardMode,
  useDashboardControlsActions,
  useDashboardMode,
} from "../../../view_stores/useDashboardControlsStore";
import { useProjectPreviewActions } from "../../../view_stores/useProjectPreviewStore";
import { ProjectPreviewControls } from "./ProjectPreviewControls";

export function ProjectDashboardModeSwitch({
  isDisabled,
}: {
  isDisabled: boolean;
}) {
  const dashboardMode = useDashboardMode();

  const { setDashboardMode } = useDashboardControlsActions();
  const { reload: reloadPreview } = useProjectPreviewActions();

  const [isPreviewHovered, setIsPreviewHovered] = useState(false);
  const [isPreviewReloadVisible, setIsPreviewReloadVisible] = useState(false);
  const [canShowLabels, setCanShowLabels] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const mouseMoveListenerRef = useRef<((e: MouseEvent) => void) | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previewBtnRef = useRef<HTMLButtonElement>(null);
  const previewDevicesControlsRef = useRef<HTMLDivElement>(null);

  const isMouseOutsidePreviewControls = (
    mouseX: number,
    mouseY: number
  ): boolean => {
    const previewBtn = previewBtnRef.current;
    const devicesControls = previewDevicesControlsRef.current;

    if (!previewBtn || !devicesControls) return true;

    const previewBtnRect = previewBtn.getBoundingClientRect();
    const devicesControlsRect = devicesControls.getBoundingClientRect();

    const isOutsidePreviewBtn =
      mouseX < previewBtnRect.left ||
      mouseX > previewBtnRect.right ||
      mouseY < previewBtnRect.top - 1 || // We need -1 because sometimes on macOS this causes the cursor to be defined as outside the Preview btn even though it's not. It happened when we entered the Preview btn from the top. The mouseY was 15 and the top of the Preview btn was 16 but the cursor was inside the button noticeably.
      mouseY > previewBtnRect.bottom;
    const isOutsideDevicesControls =
      mouseX < devicesControlsRect.left ||
      mouseX > devicesControlsRect.right ||
      mouseY < devicesControlsRect.top ||
      mouseY > devicesControlsRect.bottom;

    return isOutsidePreviewBtn && isOutsideDevicesControls;
  };

  const handlePreviewMouseEnter = (shouldShowReload: boolean) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsPreviewHovered(true);
    if (shouldShowReload && dashboardMode === DashboardMode.PREVIEW) {
      setIsPreviewReloadVisible(true);
    }

    if (!mouseMoveListenerRef.current) {
      mouseMoveListenerRef.current = (e: MouseEvent) => {
        if (isMouseOutsidePreviewControls(e.clientX, e.clientY)) {
          handlePreviewMouseLeave();
        }
      };

      document.addEventListener("mousemove", mouseMoveListenerRef.current);
    }
  };

  const handlePreviewMouseLeave = () => {
    setIsPreviewReloadVisible(false);

    if (mouseMoveListenerRef.current) {
      document.removeEventListener("mousemove", mouseMoveListenerRef.current);
      mouseMoveListenerRef.current = null;
    }

    timeoutRef.current = setTimeout(() => {
      setIsPreviewHovered(false);
    }, 300);
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setCanShowLabels(entry.contentRect.width > 240);
      }
    });

    resizeObserver.observe(containerRef.current);

    const handleWindowMouseLeave = (event: any) => {
      if (!event.relatedTarget && !event.toElement) {
        setIsPreviewReloadVisible(false);
        timeoutRef.current = setTimeout(() => {
          setIsPreviewHovered(false);
        }, 300);
      }
    };

    window.addEventListener("mouseout", handleWindowMouseLeave);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("mouseout", handleWindowMouseLeave);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (mouseMoveListenerRef.current) {
        document.removeEventListener("mousemove", mouseMoveListenerRef.current);
        mouseMoveListenerRef.current = null;
      }
      setIsPreviewHovered(false);
      setIsPreviewReloadVisible(false);
      setCanShowLabels(true);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex w-full max-w-[300px] h-10 rounded-lg bg-[#B4B7CC] dark:bg-[#191C1F]"
    >
      <div className="flex-1">
        <Btn
          label="Workspace"
          icon={<LuClipboardList size={20} />}
          isSelected={dashboardMode === DashboardMode.WORKSPACE}
          isDisabled={isDisabled}
          canShowLabel={canShowLabels}
          onClick={() => setDashboardMode(DashboardMode.WORKSPACE)}
        />
      </div>
      <div className="relative flex-1">
        <Btn
          elementRef={previewBtnRef}
          label="Preview"
          icon={<LuMonitorSmartphone size={20} />}
          isSelected={dashboardMode === DashboardMode.PREVIEW}
          isDisabled={isDisabled}
          canShowLabel={canShowLabels}
          onClick={() => setDashboardMode(DashboardMode.PREVIEW)}
          onMouseEnter={() => handlePreviewMouseEnter(true)}
          onMouseLeave={() => {
            if (!isPreviewReloadVisible) {
              handlePreviewMouseLeave();
            }
          }}
        />
        <div
          className={`absolute inset-0 bg-black/50
        flex items-center justify-center
        rounded-lg hover:cursor-pointer
        ${isPreviewReloadVisible ? "opacity-100" : "hidden"}
        transition-all duration-300`}
          onMouseLeave={handlePreviewMouseLeave}
          onClick={reloadPreview}
        >
          <LuRotateCw size={20} className="text-white" />
        </div>
        <div
          ref={previewDevicesControlsRef}
          className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 transition-all duration-300 ${
            isPreviewHovered
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          onMouseEnter={() => handlePreviewMouseEnter(false)}
          onMouseLeave={handlePreviewMouseLeave}
        >
          <ProjectPreviewControls />
        </div>
      </div>
    </div>
  );
}

function Btn({
  elementRef,
  label,
  icon,
  isSelected,
  isDisabled,
  canShowLabel,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  elementRef?: React.RefObject<HTMLButtonElement>;
  label: string;
  icon: React.ReactNode;
  isSelected: boolean;
  isDisabled: boolean;
  canShowLabel: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  return (
    <button
      ref={elementRef}
      onClick={onClick}
      disabled={isDisabled}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`${
        isSelected
          ? "bg-white dark:bg-[#272829]"
          : "bg-[#B4B7CC] dark:bg-[#191C1F] text-white dark:text-dark-text hover:text-white/50 dark:hover:text-dark-placeholder"
      } ${
        isDisabled ? "opacity-50 cursor-not-allowed" : "hover:cursor-pointer"
      } h-full w-full flex items-center justify-center gap-2 rounded-lg transition-all`}
    >
      {icon}
      <span className={canShowLabel ? "inline" : "hidden"}>{label}</span>
    </button>
  );
}
